import React from 'react';
import { Link } from 'react-router-dom';
import useComparePins from '../hooks/useComparePins';
import CompareButton from './CompareButton';
import PLACEHOLDER_IMG from '../utils/placeholderImage';
import { parcelPath } from '../utils/parcelPath';

// Server-rendered parcel preview image, requested on the AERIAL base
// (?base=aerial): an ArcGIS World_Imagery satellite tile with the
// actual parcel polygon outline drawn on top (see
// backend/landai/api/v1/preview.py). The owner asked for satellite —
// not the elevation/terrain view — on list-page cards because the
// aerial imagery is far more descriptive for finding properties
// (land-ai#1133). The endpoint still defaults to the hillshade terrain
// base for the parcel detail page's "Elevation & Terrain" viewer; the
// card explicitly opts into aerial here.
//
// Exported for unit testing. Returns null only when parcel_id is
// missing; otherwise emits a URL we hand to <img>, and the onError
// handler downgrades to the SVG placeholder if the backend 404s
// for a parcel with no geometry.
export function parcelPreviewUrl(parcelId) {
  if (!parcelId) return null;
  const base = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
    || '/api/v1';
  return `${base}/parcels/${encodeURIComponent(parcelId)}/preview.png?base=aerial`;
}

// Esri static satellite-tile URL centered on lat/lon. Free, no key.
// Kept as a tier-3 fallback for parcels where the server preview
// 404s (typically RC-XX-* listing shims without polygon geometry).
// Exported for unit testing.
export function satTileUrl(lat, lon, width = 320, height = 180) {
  // 0 is a real coordinate (equator/Greenwich) but no parcel in
  // the US sits there, so we treat exactly-zero the same as missing
  // to avoid landing the camera in the Atlantic for misencoded rows.
  if (!lat || !lon) return null;
  // Route through our backend's satellite-proxy. Direct ArcGIS fetches
  // were 5-16 s/tile from a slow CloudFront PoP per the 2026-05-28 HAR;
  // through our edge it's ~50 ms (lambda2 → ArcGIS = 30 ms, with disk
  // LRU so repeat parcels are free). The backend computes the bbox
  // from (lat, lon) so this URL stays compact.
  return `/api/v1/satellite-preview`
    + `?lat=${lat}&lon=${lon}&size=${width},${height}`;
}

// PropertyCard — unified card-style renderer for a single parcel,
// designed to be the canonical "this parcel, in a list" component
// across the site. Replaces the row-per-parcel dense tables with
// imagery-forward cards that surface price / acres / opportunity
// score at a glance and an inline Pin-for-compare control.
//
// Per the owner ask: "a list view with images would be nice. images
// on the saved list would be good too... I think the list view
// should be consistent across the site. Use a little more UI
// real-estate and make it flashier."
//
// Used by PropertyCardGrid (renders many of these in a responsive
// grid) and embedded inline on the standalone /properties page,
// SavedParcels, and the themed screens.
//
// Props:
//   parcel: the row shape returned by /parcels/table or any
//           endpoint that returns the same canonical fields
//           (parcel_id, state, county, area_acres, listing_price,
//            listing_address, days_on_market, opportunity_score,
//            mineral_score, photo_url, ...)
//   onParcelHover: optional (id|null) callback — fires on card
//           hover so the map (split view) can flash the marker
//   onUnbookmark: optional (parcel_id) callback — when present,
//           a small × button renders in the top-left of the image
//           area for removing the parcel from a saved-list context.
//           Confirmation is the caller's responsibility (the
//           SavedParcels page already does a window.confirm).
const fmt$ = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);
const fmtAcres = (v) => (v == null ? '—' : `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })} ac`);
const fmtScore = (v) => (v == null ? '—' : `${Math.round(v)}%`);

function ScoreChip({ value, label, tooltip }) {
  if (value == null) return null;
  // #1017: every score now arrives already normalized to a 0-100
  // percentile from the backend, so we render it directly. The
  // client-side min-max rescale hack (rescaleToReference /
  // OPPORTUNITY_REFERENCE_MAX) was removed once the API contract
  // changed.
  const pct = Math.round(value);
  // Color ramp: green > yellow > grey as the score drops. Tiers key
  // off the 0-100 percentile axis — the top third greens, the middle
  // ambers, the bottom greys.
  const color = pct >= 66 ? '#0e7c3a' : pct >= 40 ? '#b78103' : '#8a8a8a';
  return (
    <span
      title={tooltip || label}
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        background: color,
        color: '#fff',
        borderRadius: 10,
        fontSize: '0.68rem',
        fontWeight: 700,
        lineHeight: 1.3,
      }}
    >
      {label}: {pct}%
    </span>
  );
}

// HazardChip — inverse-color chip for the FEMA NRI overall hazard
// score (0-100, lower = safer). The user flagged that the dynamic
// range in these scores is small in practice so we want strong
// visual differentiation across tiers rather than a linear gradient.
//
// Tiers chosen against the population distribution:
//   p25 ≈ 24, p50 ≈ 49, p75 ≈ 75, p95 ≈ 95
// → "Low/Mod/High/Extreme" splits at 30/60/80 cleanly track
// quartiles for a US-listings parcel population.
function HazardChip({ value }) {
  if (value == null) return null;
  const rounded = Math.round(value);
  let bg;
  let label;
  if (rounded < 30)      { bg = '#0e7c3a'; label = 'Low'; }
  else if (rounded < 60) { bg = '#b78103'; label = 'Moderate'; }
  else if (rounded < 80) { bg = '#d35400'; label = 'High'; }
  else                   { bg = '#b71c1c'; label = 'Extreme'; }
  return (
    <span
      title={`FEMA NRI overall hazard score: ${rounded}/100 (${label})`}
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        background: bg,
        color: '#fff',
        borderRadius: 10,
        fontSize: '0.68rem',
        fontWeight: 700,
        lineHeight: 1.3,
      }}
    >
      Haz {rounded}
    </span>
  );
}

// ListingQualityChip — solid pill for the per-LISTING composite
// quality score (#775 / INIT-listing-quality). Color-ramps green /
// yellow / red so a buyer scanning search results can spot the
// high-quality listings at a glance. Renders only on the rows that
// have a populated score — listings whose score hasn't been computed
// yet (cron --new-only catches them on the next pass) just don't show
// the chip.
function ListingQualityChip({ value }) {
  if (value == null) return null;
  const rounded = Math.round(value);
  // Tier boundaries match the ParcelDetail headline donut colors and
  // the HIGH_QUALITY_THRESHOLD=60 the SearchPage filter defaults to.
  let bg;
  let label;
  if (rounded >= 75)      { bg = '#0e7c3a'; label = 'Excellent'; }
  else if (rounded >= 60) { bg = '#43a047'; label = 'Good'; }
  else if (rounded >= 45) { bg = '#b78103'; label = 'Fair'; }
  else if (rounded >= 30) { bg = '#d35400'; label = 'Poor'; }
  else                    { bg = '#b71c1c'; label = 'Very Poor'; }
  return (
    <span
      title={`Listing quality: ${rounded}/100 (${label}) — composite of photos, description, price, freshness, and completeness`}
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        background: bg,
        color: '#fff',
        borderRadius: 10,
        fontSize: '0.68rem',
        fontWeight: 700,
        lineHeight: 1.3,
      }}
      data-testid="listing-quality-chip"
    >
      Quality {rounded}
    </span>
  );
}

// UsageLimitChip — solid warning pill for data-driven usage-limitation
// tags (#153). Renders only when its condition is met so the chip strip
// stays clean on parcels without known limitations.
function UsageLimitChip({ label, color, title: titleText }) {
  return (
    <span
      title={titleText}
      style={{
        display: 'inline-block',
        padding: '2px 6px',
        background: color,
        color: '#fff',
        borderRadius: 10,
        fontSize: '0.68rem',
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: '0.01em',
      }}
    >
      {label}
    </span>
  );
}

// `footer` (optional ReactNode) renders at the bottom of the card body,
// below the compare/locate controls. It's a generic, backward-compatible
// slot for page-specific affordances that the canonical card doesn't own —
// e.g. the Research Library (#1134) drops a "research report" link + the
// dossier's datapoint/target/narrative signals here so the researched-parcel
// cards keep their report click-through without forking the card.
function PropertyCard({ parcel, onParcelHover, onUnbookmark, viewed, onFocusOnMap, footer }) {
  const { isPinned, toggle: togglePin } = useComparePins();
  const pinned = isPinned(parcel.parcel_id);

  const dom = parcel.days_on_market;
  // Image fallback chain (owner preference: satellite/aerial imagery,
  // never the elevation view — land-ai#1133):
  //   1. parcel.photo_url        — real listing photo (listing_media)
  //   2. Esri satellite tile     — preferred fallback. Most parcels
  //                                have lat/lon so this is what fires.
  //   3. /preview.png?base=aerial — rare fallback: parcel has a
  //                                parcel_id with geometry but no usable
  //                                lat/lon on the row (legacy or shim
  //                                rows). Renders the ArcGIS aerial base
  //                                with the parcel outline (not the DEM
  //                                terrain). Driven by the onError handler.
  //   4. SVG "No image"          — last resort placeholder.
  //
  // Both fallback tiers now show satellite/aerial imagery: the owner
  // reviewed the terrain vs. satellite options on the live /properties
  // page and preferred the aerial imagery — the actual ground texture
  // reads better at thumbnail size and makes properties easier to find
  // than the abstract parcel-outline-on-DEM render.
  const latitude = parcel.latitude ?? parcel.centroid?.lat;
  const longitude = parcel.longitude ?? parcel.centroid?.lon;
  const satUrl = satTileUrl(latitude, longitude);
  const previewUrl = parcelPreviewUrl(parcel.parcel_id);
  const initialPhotoUrl = parcel.photo_url
    || satUrl
    || previewUrl
    || PLACEHOLDER_IMG;
  const usingSatellite = !parcel.photo_url && initialPhotoUrl === satUrl;
  const usingPreview = !parcel.photo_url
    && !satUrl
    && initialPhotoUrl === previewUrl;
  const pricePerAcre = (parcel.listing_price && parcel.area_acres)
    ? parcel.listing_price / parcel.area_acres
    : null;

  // Which tier the initial `src` represents, so the onError chain
  // resumes from the right place rather than re-trying a tier that
  // was skipped because its URL was null. e.g. a parcel with no
  // photo_url starts on satellite; a first error should step to
  // preview, not back to satellite.
  const initialTier = parcel.photo_url
    ? 'photo'
    : (satUrl ? 'sat' : (previewUrl ? 'preview' : 'placeholder'));

  // onError downgrade chain: real photo → satellite → preview.png →
  // placeholder. Advance to the next un-tried tier regardless of which
  // tier just failed — the previous version only stepped forward when
  // the failed src happened to equal `satUrl`, so a real `photo_url`
  // that 404s jumped straight to the placeholder instead of degrading
  // to satellite like a null photo does (#1018). `data-tried` records
  // the furthest tier we've reached so we can't infinite-loop even if
  // every tier 404s.
  const onImgError = (e) => {
    const el = e.currentTarget;
    const tried = el.getAttribute('data-tried') || initialTier;
    // Walk the chain forward from wherever we are to the first tier
    // that (a) hasn't been tried and (b) has a usable URL.
    let next = tried;
    if (next === 'photo') {
      next = 'sat';
      if (satUrl) { el.setAttribute('data-tried', next); el.src = satUrl; return; }
    }
    if (next === 'sat') {
      next = 'preview';
      if (previewUrl) { el.setAttribute('data-tried', next); el.src = previewUrl; return; }
    }
    // preview failed (or no sat/preview URL available) → placeholder.
    el.setAttribute('data-tried', 'placeholder');
    el.src = PLACEHOLDER_IMG;
  };

  return (
    <article
      className="property-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        border: pinned ? '2px solid #1976d2' : '1px solid #e1e4e8',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'transform 0.1s, box-shadow 0.1s',
        // Cap card width even when the parent grid track stretches
        // wider — at wide viewports with few results, `minmax(260px,
        // 1fr)` would otherwise let a card grow to 600-800px wide
        // and the 16:9 image height grows proportionally. The cap
        // keeps cards readable + photos at a sane size regardless
        // of how much horizontal space is available. #150.
        maxWidth: 400,
        // The cap is on the card itself; the surrounding grid cell
        // still uses `1fr`, so the cards left-align in their row
        // and extra space distributes evenly via grid's
        // justify-content default ("stretch" → cells stay at the
        // track width but the card centers within via margin-auto).
        marginInline: 'auto',
        width: '100%',
      }}
      onMouseEnter={onParcelHover ? () => onParcelHover(parcel.parcel_id) : undefined}
      onMouseLeave={onParcelHover ? () => onParcelHover(null) : undefined}
    >
      <Link
        to={parcelPath(parcel.parcel_id, { state: parcel.state, county: parcel.county })}
        style={{
          position: 'relative',
          display: 'block',
          width: '100%',
          paddingTop: '56.25%',  /* 16:9 aspect-ratio reservation */
          background: '#eef2f7',
          overflow: 'hidden',
        }}
        aria-label={`View ${parcel.parcel_id} details`}
      >
        <img
          src={initialPhotoUrl}
          alt={parcel.listing_address || parcel.parcel_id}
          loading="lazy"
          data-tried={initialTier}
          onError={onImgError}
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
          }}
        />
        {/* Source label when not the listing photo. Lets users know
            they're looking at satellite/aerial imagery rather than the
            property as listed. Both fallback tiers render aerial
            imagery now (land-ai#1133) — the server preview.png tier
            requests ?base=aerial — so both badges read "SATELLITE".
            Hidden for the real photo and the SVG placeholder. */}
        {usingPreview && (
          <span
            style={{
              position: 'absolute',
              bottom: 6, right: 6,
              padding: '1px 6px',
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              borderRadius: 3,
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            SATELLITE
          </span>
        )}
        {usingSatellite && (
          <span
            style={{
              position: 'absolute',
              bottom: 6, right: 6,
              padding: '1px 6px',
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              borderRadius: 3,
              fontSize: '0.65rem',
              fontWeight: 600,
              letterSpacing: 0.3,
            }}
          >
            SATELLITE
          </span>
        )}
        {/* Price ribbon — bottom-left over the image, high-contrast */}
        {parcel.listing_price != null && (
          <span
            style={{
              position: 'absolute',
              bottom: 8, left: 8,
              padding: '4px 10px',
              background: 'rgba(0,0,0,0.78)',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 4,
              fontSize: '0.95rem',
              letterSpacing: 0.2,
            }}
          >
            {fmt$(parcel.listing_price)}
          </span>
        )}
        {/* Pin button — top-right corner, click-stop so navigation
            doesn't fire when toggling pin from the image area */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(parcel.parcel_id); }}
          aria-pressed={pinned}
          aria-label={pinned ? `Unpin ${parcel.parcel_id} from compare` : `Pin ${parcel.parcel_id} to compare`}
          title={pinned ? 'Unpin from compare bar' : 'Pin to compare bar'}
          style={{
            position: 'absolute',
            top: 6, right: 6,
            width: 28, height: 28,
            border: 'none',
            borderRadius: '50%',
            background: pinned ? '#1976d2' : 'rgba(255,255,255,0.92)',
            color: pinned ? '#fff' : '#333',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            lineHeight: 1,
          }}
        >
          {pinned ? '✓' : '+'}
        </button>
        {/* DOM badge — top-left (or shift right if the unbookmark
            button is occupying the corner). */}
        {dom != null && (
          <span
            style={{
              position: 'absolute',
              top: 6,
              left: onUnbookmark ? 40 : 6,
              padding: '2px 6px',
              background: 'rgba(255,255,255,0.92)',
              color: '#333',
              borderRadius: 3,
              fontSize: '0.7rem',
              fontWeight: 600,
            }}
          >
            {dom}d on market
          </span>
        )}
        {/* Unbookmark × — only renders when an onUnbookmark
            callback is wired (i.e. on the SavedParcels page). The
            global compare-pin still uses the top-right corner. */}
        {onUnbookmark && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUnbookmark(parcel.parcel_id); }}
            aria-label={`Remove ${parcel.parcel_id} from saved`}
            title="Remove from saved"
            style={{
              position: 'absolute',
              top: 6, left: 6,
              width: 28, height: 28,
              border: 'none',
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.92)',
              color: '#b71c1c',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        )}
      </Link>

      {/* Body */}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <Link
          to={parcelPath(parcel.parcel_id, { state: parcel.state, county: parcel.county })}
          style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 600, fontSize: '0.92rem' }}
        >
          {parcel.parcel_id}
        </Link>
        {parcel.listing_address && (
          <div style={{ fontSize: '0.78rem', color: '#444', lineHeight: 1.3 }}>
            {parcel.listing_address}
          </div>
        )}
        <div style={{ fontSize: '0.78rem', color: '#666', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span>{parcel.county ? `${parcel.county}, ` : ''}{parcel.state}</span>
          <span>·</span>
          <span>{fmtAcres(parcel.area_acres)}</span>
          {pricePerAcre != null && (
            <>
              <span>·</span>
              <span>{fmt$(pricePerAcre)}/ac</span>
            </>
          )}
        </div>
        {/* Score chips — only render the ones that have meaningful
            values so the strip stays tight on parcels with thin
            ML coverage. */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
          {viewed && (
            <UsageLimitChip
              label="Viewed"
              color="#6b7280"
              title="You've visited this parcel's detail page"
            />
          )}
          <ScoreChip
            value={parcel.opportunity_score}
            label="Opp"
            tooltip={`Composite opportunity score: ${fmtScore(parcel.opportunity_score)} (0-100 percentile)`}
          />
          <ScoreChip
            value={parcel.mineral_score}
            label="Min"
            tooltip={`Mineral ML probability: ${fmtScore(parcel.mineral_score)}`}
          />
          {/* #1017: geothermal_score is now a 0-100 percentile, so the
              "worth showing" floor is 5 (was 0.05 on the old 0-1 scale). */}
          {parcel.geothermal_score != null && parcel.geothermal_score > 5 && (
            <ScoreChip
              value={parcel.geothermal_score}
              label="Geo"
              tooltip={`Geothermal potential: ${fmtScore(parcel.geothermal_score)} (0-100 percentile)`}
            />
          )}
          {/* Hazard chip (#152) — FEMA NRI overall_score, 0-100,
              inverse-colored (low = safer = green). Tooltip carries
              the raw number + tier label. */}
          <HazardChip value={parcel.hazard_score} />
          {/* Listing-quality chip (#775 / INIT-listing-quality).
              Per-LISTING 0-100 score over photos, description,
              price, freshness, completeness. Color tier matches the
              ParcelDetail Quality card. Renders only on listings
              whose score has been computed — pre-backfill listings
              just don't show the chip. */}
          <ListingQualityChip value={parcel.listing_quality_score_0_100} />
          {/* Land-only confirmed (#610). Realie's per-component
              valuation breakdown: when total_building_value is at or
              near zero we have positive evidence (not just an
              optimistic use-code) that the dirt is what's being sold. */}
          {parcel.total_building_value != null
            && parcel.total_building_value <= 1000 && (
            <UsageLimitChip
              label="Land-only ✓"
              color="#065f46"
              title={`Realie reports $0 building value — land-only confirmed`}
            />
          )}
          {/* Owner of many (#610). owner_parcel_count ≥ 5 is a clear
              "this is a portfolio owner" signal — separates individual
              long-holders from institutional landowners. */}
          {parcel.owner_parcel_count != null
            && parcel.owner_parcel_count >= 5 && (
            <UsageLimitChip
              label={`Owns ${parcel.owner_parcel_count}`}
              color="#1e3a8a"
              title={`Owner holds ${parcel.owner_parcel_count} parcels total`
                + (parcel.owner_res_count != null
                    ? ` · ${parcel.owner_res_count} residential` : '')
                + (parcel.owner_com_count != null
                    ? ` · ${parcel.owner_com_count} commercial` : '')}
            />
          )}
          {/* Distressed-seller flag (#629). Realie's forecloseCode +
              auctionDate are the highest-conviction motivated-seller
              signals — surface as a red chip with auction date in
              the tooltip when present. */}
          {parcel.foreclose_code && (
            <UsageLimitChip
              label={parcel.auction_date ? `Auction ${parcel.auction_date.slice(5)}` : 'Distressed'}
              color="#b91c1c"
              title={`Foreclosure code ${parcel.foreclose_code}`
                + (parcel.auction_date ? ` · Auction ${parcel.auction_date}` : '')}
            />
          )}
          {/* Usage-limitation chips (#153) — data-driven warning tags.
              Each chip renders only when the signal is present/true so
              the strip stays clean on unrestricted parcels. */}
          {parcel.flood_score != null && parcel.flood_score > 70 && (
            <UsageLimitChip
              label="Flood Zone"
              color="#1565c0"
              title={`FEMA NRI flood loss score: ${Math.round(parcel.flood_score)}/100`}
            />
          )}
          {parcel.wildfire_score != null && parcel.wildfire_score > 80 && (
            <UsageLimitChip
              label="High Fire"
              color="#bf360c"
              title={`FEMA NRI wildfire loss score: ${Math.round(parcel.wildfire_score)}/100`}
            />
          )}
          {parcel.has_easement === true && (
            <UsageLimitChip
              label="Easement"
              color="#6a1a6a"
              title="Parcel has a recorded easement"
            />
          )}
          {parcel.has_lien === true && (
            <UsageLimitChip
              label="Lien"
              color="#b71c1c"
              title="Parcel has a recorded lien"
            />
          )}
          {parcel.mineral_rights_signal === 'severed' && (
            <UsageLimitChip
              label="Split Min"
              color="#37474f"
              title="Mineral rights appear severed from surface ownership"
            />
          )}
          {parcel.slope_mean != null && parcel.slope_mean > 20 && (
            <UsageLimitChip
              label="Steep"
              color="#795548"
              title={`Mean slope ${Math.round(parcel.slope_mean)}% — steep terrain limits building sites`}
            />
          )}
          {parcel.dist_to_transmission_m != null && parcel.dist_to_transmission_m > 5000 && (
            <UsageLimitChip
              label="Off-grid"
              color="#e65100"
              title={`${(parcel.dist_to_transmission_m / 1000).toFixed(1)}km from nearest grid power`}
            />
          )}
          {parcel.federal_land_agency && (
            <UsageLimitChip
              label="Fed. land"
              color="#546e7a"
              title={`Federal land: ${parcel.federal_land_agency}`}
            />
          )}
          {parcel.insar_velocity_mm_yr != null && parcel.insar_velocity_mm_yr < -5 && (
            <UsageLimitChip
              label="Subsiding"
              color="#b71c1c"
              title={`Ground subsiding ${Math.abs(Math.round(parcel.insar_velocity_mm_yr))}mm/yr (InSAR satellite)`}
            />
          )}
          {parcel.fault_dist_km != null && parcel.fault_dist_km < 5 && (
            <UsageLimitChip
              label="Active Fault"
              color="#6d4c41"
              title={`Active fault ${parcel.fault_dist_km.toFixed(1)}km away — seismic risk`}
            />
          )}
          {parcel.dist_to_road_km != null && parcel.dist_to_road_km > 1 && (
            <UsageLimitChip
              label="No Road Access"
              color="#4e342e"
              title={`${parcel.dist_to_road_km.toFixed(1)}km to nearest road — landlocked or remote`}
            />
          )}
          {parcel.not_buildable === true && (
            <UsageLimitChip
              label="Not Buildable"
              color="#4a148c"
              title="Listing description indicates parcel is not buildable"
            />
          )}
          {parcel.restricted_use === true && (
            <UsageLimitChip
              label="Restricted Use"
              color="#1a237e"
              title="Listing description indicates use restriction (easement, CC&Rs, conservation)"
            />
          )}
          {parcel.mineral_concern === true && (
            <UsageLimitChip
              label="Min. Concern"
              color="#37474f"
              title="Listing description indicates mineral rights issue (reserved, severed, split estate)"
            />
          )}
          {parcel.flood_mention === true && (
            <UsageLimitChip
              label="Flood Mention"
              color="#0d47a1"
              title="Listing description mentions flood zone or flooding risk"
            />
          )}
          {parcel.wetlands === true && (
            <UsageLimitChip
              label="Wetlands"
              color="#1b5e20"
              title="Listing description mentions wetlands, marshes, or Army Corps jurisdiction"
            />
          )}
          {(parcel.listing_match_method === 'address_fuzzy' ||
            parcel.listing_match_method === 'spatial') &&
            (parcel.listing_match_confidence == null ||
             parcel.listing_match_confidence < 0.75) && (
            <UsageLimitChip
              label="Unverified Match"
              color="#78350f"
              title={`Listing matched to this parcel by ${parcel.listing_match_method.replace('_', ' ')} (confidence: ${parcel.listing_match_confidence != null ? (parcel.listing_match_confidence * 100).toFixed(0) + '%' : 'unknown'}) — listing address may not correspond to this parcel's location`}
            />
          )}
        </div>
        {/* Explicit "+ Compare" action button (#789 / INIT-compare-listings).
            Mirrors the corner pin toggle for users who don't read the
            small "+/✓" affordance, and lets the basket pill see toggles
            from the card body. Shares state with the corner pin. */}
        <div style={{ marginTop: 6 }}>
          <CompareButton parcelId={parcel.parcel_id} variant="card" />
        </div>
        {/* #1176: render whenever we have a parcel_id — FocusFlyer fetches
            the boundary polygon (and the detail centroid as fallback) by
            parcel_id, so missing row lat/lon no longer hides the button
            and silently strands search results the map can still find. */}
        {onFocusOnMap && parcel.parcel_id != null && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFocusOnMap({
                  parcel_id: parcel.parcel_id,
                  lat: latitude ?? null,
                  lon: longitude ?? null,
                });
              }}
              title="Locate on map"
              aria-label={`Locate ${parcel.parcel_id} on map`}
              style={{
                border: '1px solid #d0d7de',
                borderRadius: 4,
                background: 'transparent',
                color: '#1976d2',
                cursor: 'pointer',
                padding: '2px 8px',
                fontSize: '0.72rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              ◎ Locate
            </button>
          </div>
        )}
        {/* Optional page-specific footer slot (#1134). Rendered inside the
            card body so it inherits the card's padding; a top divider keeps
            it visually distinct from the score/compare strip above. */}
        {footer && (
          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: '1px solid #eef1f5',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </article>
  );
}

export default PropertyCard;
